// API client del módulo public-tracking — KPIs del visitante público.
// Backend: /api/v1/admin/public-tracking/* (Bearer JWT, scope admin).

import { ensureFreshToken } from '@/lib/tokenRefresh';

const DJANGO_PROXY = '/django-api';

async function getJwt(): Promise<string> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Sesión expirada. Inicia sesión nuevamente.');
  return token;
}

async function djangoFetch<T = any>(path: string): Promise<T> {
  const jwt = await getJwt();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail || `Django ${res.status}: ${res.statusText}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return res.json();
}

const BASE = '/api/v1/admin/public-tracking';

// ── Types ─────────────────────────────────────────────────────

export type DailyPoint = { date: string; visitors: number; pageviews: number };
export type TopPathRow = { path: string; pageviews: number };

export type KpiSummary = {
  days: number;
  visitors_unique: number;
  pageviews: number;
  chat_opens: number;
  chat_messages: number;
  form_starts: number;
  form_submits: number;
  chat_open_rate: number;
  chat_message_rate: number;
  form_completion_rate: number;
  top_paths: TopPathRow[];
  per_day: DailyPoint[];
};

export type FunnelStep = { step: string; count: number; rate_from_top: number };
export type FunnelData = { days: number; steps: FunnelStep[] };

export type VisitorRow = {
  visitor_id: string;
  first_seen: string;
  last_seen: string;
  events_count: number;
  last_path: string;
  locale: string;
  country: string;
  channel?: string;
  utm_campaign?: string;
  utm_content?: string;
  has_identity: boolean;
};

export type VisitorList = {
  days: number;
  total: number;
  visitors: VisitorRow[];
};

export type TimelineEvent = {
  ts: string;
  event_type: string;
  path: string;
  props: Record<string, any>;
};

export type Timeline = {
  visitor_id: string;
  events: TimelineEvent[];
};

// ── Endpoints ─────────────────────────────────────────────────

export function fetchKpiSummary(days = 7): Promise<KpiSummary> {
  return djangoFetch<KpiSummary>(`${BASE}/kpi-summary?days=${days}`);
}

export function fetchFunnel(days = 7): Promise<FunnelData> {
  return djangoFetch<FunnelData>(`${BASE}/funnel?days=${days}`);
}

export function fetchVisitorsList(days = 7, limit = 100): Promise<VisitorList> {
  return djangoFetch<VisitorList>(`${BASE}/visitors?days=${days}&limit=${limit}`);
}

export function fetchVisitorTimeline(visitorId: string, limit = 200): Promise<Timeline> {
  return djangoFetch<Timeline>(
    `${BASE}/visitor/${encodeURIComponent(visitorId)}/timeline?limit=${limit}`,
  );
}

// ── Resultados de campaña (GET /admin/public-tracking/campaign) ─────────
// Org SIEMPRE del JWT en el backend. Sin params toma la config por datos de la
// org (seo.tracking.campaign: path_prefix, lead_sources, event_slug, launched_at).

export type CampaignSource = {
  channel: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  referrer_host: string;
  via: string;
  visitors: number;
  whatsapp_clicks: number;
  chats: number;
  conversations: number;
  form_submits: number;
  checkout_starts: number;
  purchases: number;
};

export type CampaignFunnelStep = {
  step: 'visitantes' | 'conversaciones' | 'registros' | 'pagos_iniciados' | 'pagados' | string;
  count: number;
  rate_from_prev: number;
  rate_from_top: number;
};

export type CampaignSummary = {
  org_id: string;
  path_prefix: string;
  timezone: string;
  window: { start: string; end: string; days: number };
  measuring_since: string | null;
  launched_at: string | null;
  first_party_enabled: boolean;
  visitors_unique: number;
  bots_excluded: number;
  pageviews: number;
  per_day: DailyPoint[];
  top_pages: { path: string; pageviews: number; visitors: number }[];
  sources: CampaignSource[];
  channels: { channel: string; visitors: number }[];
  whatsapp_clicks: { events: number; visitors: number };
  cta_clicks: { events: number; visitors: number; top_labels: { label: string; clicks: number }[] };
  chat: { opens: number; open_visitors: number; messages: number; message_visitors: number };
  conversations_site: { visitors: number };
  forms: { start_visitors: number; submit_visitors: number; by_kind: { kind: string; visitors: number }[] };
  registrations: {
    total: number;
    by_source: { source: string; count: number }[];
    site_form_submits: number;
    total_dedup: number;
    matched_by: string;
  };
  payments: { started: number; paid: number; seats_paid: number; by_status: { status: string; count: number }[] };
  site_checkout_starts: number;
  site_purchases: number;
  funnel: CampaignFunnelStep[];
  /** Métricas de trafficker (Meta Ads ⨝ embudo propio). null = backend sin el bloque. */
  ads?: CampaignAds | null;
};

// ── Métricas de trafficker (bloque `ads` de /campaign) ───────────────────
export type OwnFunnel = { visitors: number; conversations: number; registrations: number; payments: number; revenue: number };
export type AdCosts = {
  cost_per_visit: number | null; cost_per_conversation: number | null; cpl: number | null; cac: number | null; roas: number | null;
};
export type AdDelivery = {
  reach: number; impressions: number; frequency: number | null; clicks: number; link_clicks: number;
  ctr: number | null; cpc: number | null; cpm: number | null; spend: number;
};
export type AdRow = AdDelivery & AdCosts & { ad_id: string; ad_name: string; adset_name: string; own: OwnFunnel };
export type AdCampaignRow = AdDelivery & AdCosts & { campaign_id: string; campaign_name: string; own: OwnFunnel; ads: AdRow[] };
export type OwnUtmRow = OwnFunnel & { utm_campaign: string; utm_content: string };
export type OrganicPost = {
  id: string; ts: string; permalink?: string; caption: string; likes: number; comments: number; shares?: number;
  interactions: number; reach: number | null; type?: string;
};
export type CampaignAds =
  | { connected: false; reason?: string; own_by_utm?: OwnUtmRow[] }
  | {
      connected: true; ad_account_id: string; currency: string; revenue_currency: string; roas_comparable: boolean;
      period: { since: string; until: string }; error: string | null;
      totals: Omit<AdDelivery, 'reach' | 'frequency'> & AdCosts & { own: OwnFunnel };
      campaigns: AdCampaignRow[]; unmatched_own: OwnUtmRow[];
      organic: { facebook: OrganicPost[]; instagram: OrganicPost[]; facebook_error?: string; instagram_error?: string } | null;
    };

// ── Ficha del visitante / persona (GET /admin/public-tracking/journey) ───
export type JourneyItem = {
  ts: string; kind: 'source' | 'event' | 'lead' | 'order' | 'payment'; label: string;
  event_type?: string; path?: string; detail?: Record<string, any>;
};
export type Journey = {
  org_id: string;
  visitor: null | {
    visitor_id: string; first_seen: string; last_seen: string; attribution: Record<string, string>;
    source: { channel: string; utm_source: string; utm_medium: string; utm_campaign: string; via: string; referrer_host?: string };
    events_count: number;
  };
  pages: string[];
  leads: { id: string; name: string; email: string; phone: string; source: string; status: string; lifecycle_stage: string; created_at: string }[];
  orders: { id: string; event_slug: string; status: string; seats: number; amount: number; currency: string; created_at: string; paid_at: string | null }[];
  timeline: JourneyItem[];
  conversation_lookup: { visitor_id: string; phones: string[] };
};

export function fetchJourney(q: { visitor_id?: string; lead_id?: string }): Promise<Journey> {
  const p = new URLSearchParams();
  if (q.visitor_id) p.set('visitor_id', q.visitor_id);
  if (q.lead_id) p.set('lead_id', q.lead_id);
  return djangoFetch<Journey>(`${BASE}/journey?${p.toString()}`);
}

export type CampaignQuery = { days?: number; since?: string; path_prefix?: string };

export function fetchCampaign(q: CampaignQuery = {}): Promise<CampaignSummary> {
  const p = new URLSearchParams();
  if (q.days) p.set('days', String(q.days));
  if (q.since) p.set('since', q.since);
  if (q.path_prefix) p.set('path_prefix', q.path_prefix);
  return djangoFetch<CampaignSummary>(`${BASE}/campaign?${p.toString()}`);
}
